import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import FiltersModal from 'src/components/common/modal/FiltersModal';
import IconSvg from 'src/components/common/ui/IconSvg';
import CampaignTable from 'src/components/pages/your-campaign/CampaignTable';
import Pagination from 'src/components/pages/your-campaign/Pagination';
import { AuthContext } from 'src/contexts';
import { useCampaign } from 'src/contexts/CampaignContext';
import { Campaign } from 'src/types/campaign';

const YourCampaign: React.FC = () => {
  const navigate = useNavigate();
  const { profile } = useContext(AuthContext);
  const {
    campaigns,
    isLoading,
    currentPage,
    pageSize,
    totalItems,
    fetchCampaigns,
    setCurrentPage,
  } = useCampaign();
  const [isFiltersModalOpen, setIsFiltersModalOpen] = useState(false);

  // const currentData = campaigns.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const [filteredCampaigns, setFilteredCampaigns] = useState<Campaign[]>(campaigns);

  useEffect(() => {
    setFilteredCampaigns(campaigns);
  }, [campaigns]);

  // In your YourCampaign component
  const applyFilters = (filters: {
    status?: string;
    campaignName?: string;
    sortOrder?: 'asc' | 'desc';
    objective?: string;
  }) => {
    let result = [...campaigns];

    // Apply status filter
    if (filters.status && filters.status !== 'all') {
      result = result.filter(c => c.status === filters.status);
    }

    // Apply campaign name filter
    if (filters.campaignName) {
      result = result.filter(c =>
        c.name.toLowerCase().includes(filters?.campaignName.toLowerCase()),
      );
    }

    // Apply objective filter
    if (filters.objective && filters.objective !== 'all') {
      result = result.filter(c => c.objective === filters.objective);
    }

    // Apply sorting
    if (filters.sortOrder) {
      result.sort((a, b) => {
        const aResults = parseFloat(a.insights.results) || 0;
        const bResults = parseFloat(b.insights.results) || 0;
        return filters.sortOrder === 'asc' ? aResults - bResults : bResults - aResults;
      });
    }

    setFilteredCampaigns(result);
    setCurrentPage(1); // Reset to first page when filters change
  };
  const currentData = filteredCampaigns.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  if (!profile?.facebook) {
    return (
      <div className="relative">
        {/* Header */}
        <div className="flex justify-between items-center pb-4">
          <div>
            <p className="text-[28px] font-semibold text-black font-inter">Your Campaign</p>
            <p className="text-base font-inter font-normal text-[#4A4D4F]">
              Your Campaigns at a Glance: Review All Your Campaigns and Their Results in One Easy
              View.
            </p>
          </div>
        </div>

        {/* Modified "Ready to Launch" design for account setup */}
        <div
          className="p-96 border border-gray-200 shadow-sm flex items-center justify-center bg-no-repeat bg-center bg-contain h-[500px] w-full rounded-[24px] mt-4"
          style={{
            backgroundImage: `url('/assets/images/launch-bg.png')`,
          }}
        >
          <div className="text-center relative">
            <h1
              className="text-[96px] leading-[1] font-semibold text-black mb-12 py-px"
              style={{ fontFamily: 'Figtree' }}
            >
              Oops!
            </h1>

            <div className="relative inline-block -mt-0 -ml-6">
              <button
                onClick={() => navigate('/dashboard/account-setup')}
                className="text-white bg-blue-600 hover:bg-blue-700 relative z-10 shadow-lg"
                style={{
                  width: '619.67px',
                  height: '132px',
                  borderRadius: '24.44px',
                  paddingTop: '34.22px',
                  paddingBottom: '34.22px',
                  paddingLeft: '58.67px',
                  paddingRight: '58.67px',
                  fontFamily: 'Inter',
                  fontWeight: 500,
                  fontSize: '40px',
                  lineHeight: '56.22px',
                  letterSpacing: '-0.01em',
                  textAlign: 'center',
                }}
              >
                Set Up Facebook Account
              </button>

              <div className="absolute -bottom-12 -right-0 z-20 pointer-events-none drop-shadow-[0_0_12px_white]">
                <IconSvg icon="mouse" />
              </div>
            </div>

            <p className="mt-20 text-xl text-gray-600">
              You need to connect your Facebook account before launching a campaign.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="">
      <div className="flex justify-between items-center pb-4">
        <div>
          <p className="text-[28px] font-semibold text-black font-inter">Your Campaign</p>
          <p className="text-base font-inter font-normal text-[#4A4D4F]">
            Your Campaigns at a Glance: Review All Your Campaigns and Their Results in One Easy
            View.
          </p>
        </div>
        <div className="flex items-center space-x-4">
          <button
            type="button"
            onClick={() => setIsFiltersModalOpen(true)}
            className="bg-[#00000014] text-black px-4 py-2 rounded-[10px] text-base font-medium flex items-center"
          >
            <span className="mr-2">
              <IconSvg icon="filter" />
            </span>{' '}
            Filters
          </button>

          <button
            type="button"
            onClick={() => navigate('/dashboard/create-campaign')}
            className="bg-[#6277FF] text-white px-4 py-2 rounded-[10px] text-base font-medium flex items-center"
          >
            <span className="mr-2">
              <IconSvg icon="plus" />
            </span>{' '}
            Create new campaign
          </button>
        </div>
      </div>

      <CampaignTable data={currentData} isLoading={isLoading} />

      <Pagination
        currentPage={currentPage}
        totalItems={filteredCampaigns.length}
        pageSize={pageSize}
        onPageChange={handlePageChange}
      />

      {isFiltersModalOpen && (
        <FiltersModal
          onClose={() => setIsFiltersModalOpen(false)}
          onApplyFilters={applyFilters}
          campaigns={campaigns}
        />
      )}
    </div>
  );
};

export default YourCampaign;
